import {
  attachmentDownloadPath,
  fetchAuthorizedMediaBlob,
  parseAttachments,
  readFileAsBase64Payload,
} from '@/lib/realtime/message-attachments';
import { sendConversationMessage } from '@/lib/realtime/chat-realtime-service';
import { getMessageText } from '@/lib/messages/messages-helpers';
import { parseMessageBodyText } from '@/lib/realtime/chat-message-helpers';

/**
 * Forwarding a message = sending a CLEAN copy into another thread over the normal WS send
 * path, with the provenance stamped on `metadata.forwarded`. Only body text and attachments
 * travel; everything tied to the source row (externalId, reactions, receipts,
 * metadata.whatsapp, …) must NOT be copied — externalId in particular would make the
 * backend's upsert-by-externalId OVERWRITE the source message instead of inserting a copy.
 *
 * The stamp: { sourceThreadId, sourceMessageId, sourceScope: 'patient'|'internal', sourceTitle }.
 * `sourceTitle` is a display snapshot taken at forward time (like a pin snapshot) so the
 * marker renders without loading the source thread. Rendering rules live in the cluster
 * list: markers show in internal chats only — a patient must only ever see the plain
 * message — and only patient-sourced markers link back to the conversation point.
 *
 * A forward can carry SEVERAL messages to one destination (`forwardMessagesToThread`): they go
 * out as separate messages, one stamp each, never merged into a single body.
 */

/**
 * How many messages one forward may carry. The ceiling is the attachment re-encode: every
 * selected message's media is re-downloaded and held in memory as base64 (see
 * buildForwardAttachmentPayloads), so an unbounded selection is an unbounded memory spike.
 */
export const MAX_FORWARD_SELECTION = 30;

/**
 * Pause between the WS frames of one batch. The server stamps `createdAt` on arrival, so
 * back-to-back frames can tie on the millisecond and sort unpredictably in the destination;
 * the gap also keeps the WhatsApp gateway from being handed a dozen media sends in one tick.
 */
const FORWARD_SEND_GAP_MS = 60;

/** Normalized `metadata.forwarded` of a message, or null when it isn't a forward. */
export function getForwardedInfo(message) {
  const raw = message?.metadata?.forwarded;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  return {
    sourceThreadId: String(raw.sourceThreadId || '').trim(),
    sourceMessageId: String(raw.sourceMessageId || '').trim(),
    sourceScope: raw.sourceScope === 'patient' ? 'patient' : 'internal',
    sourceTitle: String(raw.sourceTitle || '').trim(),
  };
}

/**
 * The `metadata.forwarded` stamp for a new forward of `message`. Re-forwarding an already
 * forwarded message keeps the ORIGINAL stamp, so the marker keeps pointing at the true
 * origin (and a patient-sourced link survives hops through internal chats).
 *
 * Always per message — a batch stamps each of its messages separately, so a selection that
 * mixes fresh and already-forwarded messages keeps every backlink correct.
 *
 * @param {{ message: object, sourceThreadId?: string, sourceScope: 'patient'|'internal', sourceTitle?: string }} args
 */
export function buildForwardedStamp({ message, sourceThreadId, sourceScope, sourceTitle }) {
  const prior = getForwardedInfo(message);
  if (prior && (prior.sourceThreadId || prior.sourceMessageId)) {
    return prior;
  }
  return {
    sourceThreadId: String(message?.threadId || sourceThreadId || '').trim(),
    sourceMessageId: message?.id ? String(message.id) : '',
    sourceScope: sourceScope === 'patient' ? 'patient' : 'internal',
    sourceTitle: String(sourceTitle || '').trim(),
  };
}

/**
 * Re-encode the source message's attachments as inline base64 payloads for the WS send.
 * Stored media is re-downloaded and re-uploaded rather than referenced by fileId: media
 * download auth is scoped to the file's ORIGINAL thread, so a by-reference copy would 403
 * for recipients (and for the WhatsApp gateway) that can't access the source thread.
 * Re-ingesting stamps a fresh file on the destination thread instead.
 */
export async function buildForwardAttachmentPayloads(message) {
  const attachments = parseAttachments(message);
  const payloads = [];
  for (const att of attachments) {
    if (!att || typeof att !== 'object') continue;
    const filename = String(att.filename || 'attachment');
    const inline = att.data != null ? String(att.data).trim() : '';
    if (inline) {
      payloads.push({
        mimeType: String(att.mimeType || 'application/octet-stream'),
        data: inline,
        filename,
      });
      continue;
    }
    const path = attachmentDownloadPath(att);
    if (!path) continue;
    const blob = await fetchAuthorizedMediaBlob(path);
    const mimeType = String(att.mimeType || att.contentType || blob.type || 'application/octet-stream');
    payloads.push(await readFileAsBase64Payload(new File([blob], filename, { type: mimeType })));
  }
  return payloads;
}

/**
 * The body text a forwarded copy should actually carry.
 *
 * getMessageText is a DISPLAY helper: for a message with neither text nor attachments it returns
 * the placeholder 'No content' so the bubble isn't blank. Forwarding that would put the literal
 * words 'No content' in the destination — and on WhatsApp, in front of a patient. So the display
 * string is trusted only when the raw body has something in it; otherwise the message has nothing
 * to forward at all. (getMessageText still does the work when there IS a body: it formats WhatsApp
 * group system lines, which the raw parser renders as a bare event name.)
 */
function forwardableText(message) {
  if (!String(parseMessageBodyText(message?.body) || '').trim()) {
    return '';
  }
  return String(getMessageText(message) || '').trim();
}

/**
 * Everything one forwarded copy needs on the wire, or null when the message carries nothing
 * sendable (no text and no resolvable attachment). Doing the media work here — before any
 * frame goes out — is what lets a batch fail without leaving half of itself in the destination.
 *
 * @param {{ message: object, forwarded: ReturnType<typeof buildForwardedStamp> }} item
 */
export async function buildForwardPayload({ message, forwarded }) {
  const text = forwardableText(message);
  const attachments = await buildForwardAttachmentPayloads(message);
  if (!text && attachments.length === 0) {
    return null;
  }
  return { text, attachments, metadata: { forwarded } };
}

/**
 * Forward one or more messages into a single thread. `items` must already be in conversation
 * order — the sends fire in array order and that is the order they land in.
 *
 * Prepare-then-send, deliberately: buildForwardPayload re-downloads media, so a 403 or a
 * dropped connection partway through a naive send loop would leave half the batch sitting in
 * the destination with no way to tell which half. Building every payload first makes a
 * preparation failure all-or-nothing.
 *
 * Resolves once the frames are handed to the socket (the WS echo delivers the stored messages
 * to every member, including us).
 *
 * @param {{
 *   threadId: string,
 *   items: { message: object, forwarded: ReturnType<typeof buildForwardedStamp> }[],
 *   onProgress?: (done: number, total: number) => void,
 * }} args
 * @returns {Promise<{ sent: number, skipped: number }>} skipped = messages with nothing to send.
 */
export async function forwardMessagesToThread({ threadId, items, onProgress }) {
  const tid = String(threadId || '').trim();
  if (!tid) {
    throw new Error('Could not resolve the destination conversation.');
  }
  const list = (Array.isArray(items) ? items : []).filter((item) => item && item.message);
  if (list.length === 0) {
    throw new Error('This message has no content that can be forwarded.');
  }
  if (list.length > MAX_FORWARD_SELECTION) {
    throw new Error(`You can forward at most ${MAX_FORWARD_SELECTION} messages at once.`);
  }

  // Prepare: every payload built before the first frame goes out.
  const payloads = [];
  let skipped = 0;
  for (let index = 0; index < list.length; index += 1) {
    const payload = await buildForwardPayload(list[index]);
    if (payload) {
      payloads.push(payload);
    } else {
      skipped += 1;
    }
    onProgress?.(index + 1, list.length);
  }
  if (payloads.length === 0) {
    throw new Error(
      list.length === 1
        ? 'This message has no content that can be forwarded.'
        : 'None of the selected messages have content that can be forwarded.',
    );
  }

  // Send: in order, spaced, so the copies land in the order they were written.
  let sent = 0;
  for (const payload of payloads) {
    if (sent > 0) {
      await new Promise((resolve) => {
        setTimeout(resolve, FORWARD_SEND_GAP_MS);
      });
    }
    try {
      sendConversationMessage(tid, payload.text, {
        attachments: payload.attachments,
        metadata: payload.metadata,
      });
    } catch (error) {
      // The realistic cause is the socket going down mid-batch. Anything already sent is
      // really in the destination, so say so instead of reporting a clean failure.
      if (sent === 0) throw error;
      const partial = new Error(
        `Forwarded ${sent} of ${payloads.length} messages before the connection dropped. Forward the rest again.`,
      );
      partial.sent = sent;
      throw partial;
    }
    sent += 1;
  }
  return { sent, skipped };
}

/**
 * Send the forwarded copy of a single `message` into `threadId`.
 *
 * @param {{ threadId: string, message: object, forwarded: ReturnType<typeof buildForwardedStamp> }} args
 */
export async function forwardMessageToThread({ threadId, message, forwarded }) {
  await forwardMessagesToThread({ threadId, items: [{ message, forwarded }] });
}
